import { supabase } from '@/core/database/supabase';
import {
  emptyWorkOrderDetail,
  type DetailRow,
  type WorkOrderDetailState,
} from '../types/workOrderDetail.types';

export const workOrderDetailService = {
  async getWorkOrderDetail(id: string, companyId?: string): Promise<WorkOrderDetailState> {
    if (!id) return emptyWorkOrderDetail;

    const { data: order, error } = await supabase
      .from('work_orders')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error || !order) {
      console.warn('[workOrderDetailService] order fallback:', error);
      return emptyWorkOrderDetail;
    }

    const companyFilter = companyId || order.company_id;
    const [
      project,
      zone,
      phase,
      boqItem,
      team,
      lead,
      tasks,
      members,
      materials,
      equipment,
      checklists,
      photos,
      logs,
      inspections,
    ] = await Promise.all([
      maybeSingle('projects', order.project_id, 'id, code, name'),
      maybeSingle('project_zones', order.zone_id, 'id, project_id, zone_code, zone_name'),
      maybeSingle('project_phases', order.phase_id, 'id, project_id, code, name'),
      maybeSingle('construction_boq_items', order.boq_item_id, 'id, item_code, item_name, description, specification, unit, quantity'),
      maybeSingle('teams', order.assigned_team_id || order.team_id, 'id, code, name, leader_user_id'),
      maybeSingle('user_profiles', order.lead_user_id, 'id, full_name, email, avatar_path, role_code'),
      maybeList(
        supabase.from('construction_tasks').select('*').eq('work_order_id', id).order('sort_order', { ascending: true }),
        'tasks',
      ),
      maybeList(
        supabase
          .from('work_order_members')
          .select('*, user:user_profiles(id, full_name, email, avatar_path, role_code)')
          .eq('work_order_id', id)
          .order('created_at', { ascending: true }),
        'members',
      ),
      maybeList(
        supabase.from('work_order_materials').select('*').eq('work_order_id', id).order('created_at', { ascending: true }),
        'materials',
      ),
      maybeList(
        supabase.from('work_order_equipment').select('*').eq('work_order_id', id).order('created_at', { ascending: true }),
        'equipment',
      ),
      maybeList(
        supabase.from('construction_work_order_checklists').select('*').eq('work_order_id', id).order('sort_order', { ascending: true }),
        'checklists',
      ),
      maybeList(
        supabase.from('construction_work_order_photos').select('*').eq('work_order_id', id).order('created_at', { ascending: false }),
        'photos',
      ),
      maybeList(
        supabase
          .from('progress_logs')
          .select('*, user:user_profiles(full_name)')
          .eq('work_order_id', id)
          .eq('company_id', companyFilter)
          .order('created_at', { ascending: false }),
        'logs',
      ),
      maybeList(
        supabase.from('construction_qa_inspections').select('*').eq('work_order_id', id).order('created_at', { ascending: false }),
        'inspections',
      ),
    ]);

    return {
      order,
      project,
      zone,
      phase,
      boqItem,
      team,
      lead,
      tasks,
      members,
      materials,
      equipment,
      checklists,
      photos,
      logs,
      inspections,
    };
  },

  async assignResources(
    workOrderId: string, 
    projectId: string, 
    companyId: string, 
    resources: { type: 'material' | 'equipment'; items: any[] }
  ) {
    if (resources.type === 'material') {
      const { error } = await supabase
        .from('work_order_materials')
        .insert(resources.items.map(item => ({
          work_order_id: workOrderId,
          project_id: projectId,
          company_id: companyId,
          material_name: item.name,
          planned_qty: item.quantity,
          unit: item.unit || 'lot',
          status_code: 'assigned'
        })));
      if (error) throw error;
    } else {
      const { error } = await supabase
        .from('work_order_equipment')
        .insert(resources.items.map(item => ({
          work_order_id: workOrderId,
          project_id: projectId,
          company_id: companyId,
          equipment_name: item.name,
          planned_hours: item.hours,
          status_code: 'assigned'
        })));
      if (error) throw error;
    }
  },

  async removeResource(id: string, type: 'material' | 'equipment') {
    const table = type === 'material' ? 'work_order_materials' : 'work_order_equipment';
    const { error } = await supabase.from(table).delete().eq('id', id);
    if (error) throw error;
  },

  async updateResourceQuantity(id: string, type: 'material' | 'equipment', quantity: number) {
    const table = type === 'material' ? 'work_order_materials' : 'work_order_equipment';
    const field = type === 'material' ? 'planned_qty' : 'planned_hours';
    const { error } = await supabase.from(table).update({ [field]: quantity }).eq('id', id);
    if (error) throw error;
  }
};

async function maybeSingle(table: string, id?: string | null, columns = '*') {
  if (!id) return null;
  const { data, error } = await supabase.from(table).select(columns).eq('id', id).maybeSingle();
  if (error) {
    console.warn(`[workOrderDetailService] ${table} fallback:`, error);
    return null;
  }
  return data;
}

async function maybeList(query: any, label: string): Promise<DetailRow[]> {
  const { data, error } = await query;
  if (error) {
    console.warn(`[workOrderDetailService] ${label} fallback:`, error);
    return [];
  }
  return Array.isArray(data) ? data : [];
}
